import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../services/api';
import ClientLayout from '../../components/ClientLayout';
import { Loader2, Filter, X, ZoomIn } from 'lucide-react';

const ITEM_TYPES = [
  { value: '', label: 'Все типы' },
  { value: 'верхняя одежда', label: 'Верхняя одежда' },
  { value: 'верх', label: 'Верх' },
  { value: 'низ', label: 'Низ' },
  { value: 'платье', label: 'Платье' },
  { value: 'обувь', label: 'Обувь' },
  { value: 'аксессуар', label: 'Аксессуар' },
  { value: 'сумка', label: 'Сумка' },
  { value: 'другое', label: 'Другое' },
];

const SEASONS = [
  { value: '', label: 'Все сезоны' },
  { value: 'лето', label: 'Лето' },
  { value: 'зима', label: 'Зима' },
  { value: 'межсезонье', label: 'Межсезонье' },
  { value: 'круглогодичная', label: 'Круглогодичная' },
];

const ClientWardrobe = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  
  const navigate = useNavigate();
  const clientId = localStorage.getItem('clientId');

  useEffect(() => {
    if (!clientId) {
      navigate('/cabinet/login');
      return;
    }
    fetchWardrobe();
  }, [clientId, navigate, filterType, filterSeason]);

  const fetchWardrobe = async () => {
    setLoading(true);
    try {
      const data = await clientAPI.getWardrobe(clientId, {
        itemType: filterType,
        season: filterSeason
      });
      setItems(data);
    } catch (err) {
      console.error('Error fetching wardrobe:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterType('');
    setFilterSeason('');
  };

  const hasActiveFilters = filterType || filterSeason;

  return (
    <ClientLayout>
      <div className="wardrobe-page" data-testid="wardrobe-page">
        {/* Header */}
        <div className="wardrobe-header">
          <div>
            <h1 className="wardrobe-title">Мой гардероб</h1>
            <p className="wardrobe-count">
              {items.length} {items.length === 1 ? 'вещь' : items.length < 5 ? 'вещи' : 'вещей'}
            </p>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle-btn"
            className={`filter-btn ${hasActiveFilters ? 'active' : ''}`}
          >
            <Filter size={16} />
            Фильтры
            {hasActiveFilters && (
              <span className="filter-count">
                {(filterType ? 1 : 0) + (filterSeason ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel" data-testid="filters-panel">
            <div className="filters-grid">
              <div>
                <label className="filter-label">Тип вещи</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  data-testid="filter-type-select"
                  className="filter-select"
                >
                  {ITEM_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="filter-label">Сезон</label>
                <select
                  value={filterSeason}
                  onChange={(e) => setFilterSeason(e.target.value)}
                  data-testid="filter-season-select"
                  className="filter-select"
                >
                  {SEASONS.map((season) => (
                    <option key={season.value} value={season.value}>{season.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters} className="clear-filters-btn">
                <X size={14} />
                Сбросить фильтры
              </button>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="loading-container">
            <Loader2 size={32} className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <p className="empty-title">
              {hasActiveFilters ? 'Ничего не найдено' : 'Гардероб пуст'}
            </p>
            <p className="empty-text">
              {hasActiveFilters 
                ? 'Попробуйте изменить фильтры'
                : 'Ваш стилист добавит вещи после консультации'
              }
            </p>
          </div>
        ) : (
          <div className="masonry-grid" data-testid="wardrobe-grid">
            {items.map((item) => (
              <div
                key={item.id}
                className="masonry-item"
                data-testid={`wardrobe-item-${item.id}`}
              >
                <div 
                  className="item-image-container"
                  onClick={() => setFullscreenImage(item.image_url)}
                >
                  <img
                    src={item.image_url}
                    alt={item.item_kind}
                    className="item-image"
                  />
                  <div className="zoom-overlay">
                    <ZoomIn size={24} />
                  </div>
                </div>
                <div className="item-info" onClick={() => setSelectedItem(item)}>
                  <p className="item-name">{item.item_kind}</p>
                  <p className="item-season">{item.season}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Item Detail Modal */}
        {selectedItem && (
          <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="item-detail-modal">
              <div 
                className="modal-image-container"
                onClick={() => setFullscreenImage(selectedItem.image_url)}
              >
                <img
                  src={selectedItem.image_url}
                  alt={selectedItem.item_kind}
                  className="modal-image"
                />
                <div className="zoom-overlay">
                  <ZoomIn size={32} />
                </div>
              </div>
              <div className="modal-info">
                <h3 className="modal-title">{selectedItem.item_kind}</h3>
                
                <div className="modal-details">
                  <div>
                    <span className="detail-label">Тип</span>
                    <p className="detail-value">{selectedItem.item_type}</p>
                  </div>
                  <div>
                    <span className="detail-label">Сезон</span>
                    <p className="detail-value">{selectedItem.season}</p>
                  </div>
                </div>

                {selectedItem.notes && (
                  <div>
                    <span className="detail-label">Заметки</span>
                    <p className="detail-value notes">{selectedItem.notes}</p>
                  </div>
                )}

                <button onClick={() => setSelectedItem(null)} className="close-btn">
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Image Viewer */}
        {fullscreenImage && (
          <div 
            className="fullscreen-viewer"
            onClick={() => setFullscreenImage(null)}
          >
            <button className="fullscreen-close">
              <X size={32} />
            </button>
            <img 
              src={fullscreenImage} 
              alt="Full size" 
              className="fullscreen-image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <style>{`
          .wardrobe-page {
            max-width: 1200px;
            padding: 0 var(--space-m);
          }
          
          .wardrobe-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: var(--space-xl);
            flex-wrap: wrap;
            gap: var(--space-m);
          }
          
          .wardrobe-title {
            font-family: var(--font-display);
            font-size: clamp(1.5rem, 4vw, 2.5rem);
            font-weight: 300;
            margin-bottom: var(--space-s);
          }
          
          .wardrobe-count {
            font-family: var(--font-body);
            color: var(--mid-gray);
            font-size: 0.875rem;
          }
          
          .filter-btn {
            display: flex;
            align-items: center;
            gap: var(--space-s);
            padding: var(--space-m) var(--space-l);
            background-color: var(--white);
            color: var(--charcoal);
            border: 1px solid var(--soft-gray);
            font-family: var(--font-body);
            font-size: 0.8125rem;
            cursor: pointer;
            transition: all 200ms ease;
          }
          
          .filter-btn.active {
            background-color: var(--black);
            color: var(--white);
          }
          
          .filter-count {
            background-color: var(--accent-gold);
            color: var(--black);
            border-radius: 50%;
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.75rem;
          }
          
          .filters-panel {
            background-color: var(--white);
            padding: var(--space-l);
            margin-bottom: var(--space-xl);
            border: 1px solid var(--soft-gray);
          }
          
          .filters-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: var(--space-m);
          }
          
          @media (min-width: 480px) {
            .filters-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .filter-label {
            display: block;
            font-family: var(--font-body);
            font-size: 0.75rem;
            letter-spacing: 1px;
            text-transform: uppercase;
            color: var(--mid-gray);
            margin-bottom: var(--space-s);
          }
          
          .filter-select {
            width: 100%;
            padding: 12px 16px;
            font-family: var(--font-body);
            font-size: 0.875rem;
            border: 1px solid var(--soft-gray);
            background-color: var(--white);
            cursor: pointer;
          }
          
          .clear-filters-btn {
            display: flex;
            align-items: center;
            gap: var(--space-s);
            margin-top: var(--space-l);
            background: none;
            border: none;
            font-family: var(--font-body);
            font-size: 0.8125rem;
            color: var(--mid-gray);
            cursor: pointer;
          }
          
          .loading-container {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 300px;
          }
          
          .spinner {
            color: var(--accent-gold);
            animation: spin 1s linear infinite;
          }
          
          .empty-state {
            text-align: center;
            padding: var(--space-xxl);
            background-color: var(--white);
            border: 1px solid var(--soft-gray);
          }
          
          .empty-title {
            font-family: var(--font-display);
            font-size: 1.5rem;
            margin-bottom: var(--space-m);
          }
          
          .empty-text {
            font-family: var(--font-body);
            font-size: 0.875rem;
            color: var(--mid-gray);
          }
          
          /* Masonry Grid */
          .masonry-grid {
            columns: 2;
            column-gap: var(--space-m);
          }
          
          @media (min-width: 600px) {
            .masonry-grid {
              columns: 3;
            }
          }
          
          @media (min-width: 900px) {
            .masonry-grid {
              columns: 4;
            }
          }
          
          .masonry-item {
            break-inside: avoid;
            margin-bottom: var(--space-m);
            background-color: var(--white);
            border: 1px solid var(--soft-gray);
            overflow: hidden;
            transition: all 300ms ease;
          }
          
          .masonry-item:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          }
          
          .item-image-container {
            position: relative;
            cursor: pointer;
            overflow: hidden;
          }
          
          .item-image {
            width: 100%;
            height: auto;
            display: block;
          }
          
          .zoom-overlay {
            position: absolute;
            inset: 0;
            background: rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 200ms ease;
            color: white;
          }
          
          .item-image-container:hover .zoom-overlay {
            opacity: 1;
          }
          
          .item-info {
            padding: var(--space-m);
            cursor: pointer;
          }
          
          .item-name {
            font-family: var(--font-body);
            font-size: 0.875rem;
            font-weight: 500;
            margin-bottom: 4px;
            text-transform: capitalize;
          }
          
          .item-season {
            font-family: var(--font-body);
            font-size: 0.75rem;
            color: var(--light-gray);
          }
          
          /* Modal */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0,0,0,0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--space-m);
            z-index: 1000;
          }
          
          .modal-content {
            background-color: var(--white);
            max-width: 500px;
            width: 100%;
            max-height: 90vh;
            overflow: auto;
          }
          
          .modal-image-container {
            position: relative;
            cursor: pointer;
            background-color: var(--cream);
          }
          
          .modal-image {
            width: 100%;
            height: auto;
            display: block;
          }
          
          .modal-info {
            padding: var(--space-l);
          }
          
          .modal-title {
            font-family: var(--font-display);
            font-size: 1.5rem;
            font-weight: 300;
            margin-bottom: var(--space-m);
            text-transform: capitalize;
          }
          
          .modal-details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-m);
            margin-bottom: var(--space-l);
          }
          
          .detail-label {
            font-family: var(--font-body);
            font-size: 0.75rem;
            color: var(--light-gray);
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .detail-value {
            font-family: var(--font-body);
            font-size: 0.875rem;
            margin-top: 4px;
            text-transform: capitalize;
          }
          
          .detail-value.notes {
            text-transform: none;
            color: var(--charcoal);
          }
          
          .close-btn {
            width: 100%;
            margin-top: var(--space-l);
            padding: 14px;
            background-color: var(--black);
            color: var(--white);
            border: none;
            font-family: var(--font-body);
            font-size: 0.8125rem;
            letter-spacing: 1px;
            text-transform: uppercase;
            cursor: pointer;
          }
          
          /* Fullscreen Viewer */
          .fullscreen-viewer {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0,0,0,0.95);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2000;
            padding: var(--space-l);
          }
          
          .fullscreen-close {
            position: absolute;
            top: var(--space-l);
            right: var(--space-l);
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            padding: var(--space-s);
          }
          
          .fullscreen-image {
            max-width: 100%;
            max-height: 90vh;
            object-fit: contain;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </ClientLayout>
  );
};

export default ClientWardrobe;
